import { useState } from 'react'
import { Infinity as InfinityIcon } from 'lucide-react'
import { startPayfastCheckout } from '@/lib/payfastCheckout'
import {
  TOKEN_PACKS,
  TOKEN_PACK_PRICES_ZAR,
  formatCount,
  formatZar,
  type TokenPackKey,
} from '@/lib/billingPlans'

interface TopUpPacksProps {
  // mt_chars_extra from public.user_usage, or null while it is unknown.
  extraChars: number | null
  hasBoughtPack: boolean
}

export default function TopUpPacks({ extraChars, hasBoughtPack }: TopUpPacksProps) {
  const [pending, setPending] = useState<TokenPackKey | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleBuy = async (pack: TokenPackKey) => {
    setError(null)
    setPending(pack)
    try {
      await startPayfastCheckout('purchase-pack', { pack })
      // The browser is now leaving for PayFast.
    } catch (err: any) {
      setError(err.message || 'Something went wrong starting checkout. Please try again.')
      setPending(null)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <InfinityIcon className="w-4 h-4 text-gold-600" />
        Top-up packs
      </h2>
      <p className="text-sm text-gray-800 mb-4">
        One-off purchases of extra machine translation characters. They never expire and are used after your daily and monthly allowance.
      </p>

      {extraChars !== null && (extraChars > 0 || hasBoughtPack) && (
        <p className="text-sm text-gray-900 mb-3">
          Extra characters remaining: <span className="font-semibold">{extraChars.toLocaleString('en-GB')}</span>
        </p>
      )}

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="space-y-2">
        {TOKEN_PACKS.map(pack => (
          <div key={pack.key} className="flex items-center justify-between rounded-xl border border-gray-200 p-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">{pack.name} pack</div>
              <div className="text-xs text-gray-600">{formatCount(pack.chars)} extra MT characters, one-off</div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-gray-900">{formatZar(TOKEN_PACK_PRICES_ZAR[pack.key])}</span>
              <button
                type="button"
                onClick={() => handleBuy(pack.key)}
                disabled={pending !== null}
                className="px-4 py-1.5 rounded-lg bg-gold-500 hover:bg-gold-400 text-slate-900 text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {pending === pack.key ? 'Redirecting…' : 'Buy'}
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">Prices are in South African rand, paid once through PayFast.</p>
    </div>
  )
}
